// ── OrgRiskAcceptanceDetailPage ───────────────────────────────────────────────
//
// Route: /org-risk-acceptances/:id
//
// Parallel to RiskAcceptanceDetailPage (D-ORA-ARCH-7 structural parallelism).
// ORA-specific differences:
//   - Uses ORA hooks + OraEntry type
//   - ORA.5: RequestRenewalModal present (Submitter + APPROVED + window open)
//   - ORA.9: OrgRenewalLineageSection inserted after grid (parallel to vendor RA-FU-7)
//   - Copy-to-clipboard URL: /org-risk-acceptances/:id
//   - Navigation paths: /org-risk-acceptances/*
//   - LinkedEntityDisplay organization branch now live

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ResourceNotes } from '@/components/notes/ResourceNotes';
import { toast } from 'sonner';
import { formatDistanceToNow, format } from 'date-fns';
import {
  ChevronLeft,
  Copy,
  CheckCircle,
  XCircle,
  RotateCcw,
  Clock,
  AlertCircle,
  FileEdit,
  Send,
  Loader2,
  ShieldAlert,
  RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { RrStatusCell } from '@/components/risk-acceptances/RrStatusCell';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/store/auth.store';
import {
  useOrgRiskAcceptance,
  useSubmitOrgRiskAcceptance,
  useResumeOrgDraft,
  useOrgRiskAcceptanceAction,
  useRequestOrgRenewal,
} from '@/api/hooks/useOrgRiskAcceptances';
import { RiskAcceptanceActionModal } from '@/components/shared/RiskAcceptanceActionModal';
import { RequestRenewalModal } from '@/components/shared/RequestRenewalModal';
import { LinkedEntityDisplay } from '@/components/risk-acceptances/LinkedEntityDisplay';
import { OrgRenewalLineageSection } from '@/components/org-risk-acceptances/OrgRenewalLineageSection';
import type { OraEntry } from '@/api/types/org-risk-acceptance.types';

// ── Field display helpers ─────────────────────────────────────────────────────

function FieldRow({ label, value }: { label: string; value: string | null | undefined }) {
  if (!value) return null;
  return (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-text-secondary">{label}</dt>
      <dd className="mt-1 text-sm text-text-primary sm:col-span-2 sm:mt-0 whitespace-pre-wrap break-words">
        {value}
      </dd>
    </div>
  );
}

function BoolRow({ label, value }: { label: string; value: boolean | null }) {
  if (value == null) return null;
  return (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-text-secondary">{label}</dt>
      <dd className="mt-1 text-sm text-text-primary sm:col-span-2 sm:mt-0">
        {value ? 'Yes' : 'No'}
      </dd>
    </div>
  );
}

function DateRow({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="py-2 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-text-secondary">{label}</dt>
      <dd className="mt-1 text-sm text-text-primary sm:col-span-2 sm:mt-0">
        {format(new Date(value), 'PPP')}
      </dd>
    </div>
  );
}

// ── Audit timeline helpers ────────────────────────────────────────────────────

interface TimelineEvent {
  label: string;
  timestamp: string;
  icon: React.ElementType;
  iconClass: string;
  detail?: string;
}

// IF-4: deriveTimeline copied verbatim from vendor RA detail page (identical state names)
function deriveTimeline(entry: OraEntry): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  events.push({
    label: 'Draft created',
    timestamp: entry.created_at,
    icon: FileEdit,
    iconClass: 'text-text-secondary',
  });

  if (entry.submitted_at) {
    events.push({
      label: 'Submitted for approval',
      timestamp: entry.submitted_at,
      icon: Send,
      iconClass: 'text-accent-cyan',
    });
  }

  if (entry.approved_at) {
    events.push({
      label: 'Approved',
      timestamp: entry.approved_at,
      icon: CheckCircle,
      iconClass: 'text-accent-emerald',
      detail: entry.approval_notes ? `Notes: ${entry.approval_notes.slice(0, 120)}${entry.approval_notes.length > 120 ? '…' : ''}` : undefined,
    });
  }

  if (entry.denied_at) {
    events.push({
      label: 'Denied',
      timestamp: entry.denied_at,
      icon: XCircle,
      iconClass: 'text-accent-rose',
      detail: entry.denial_reason ? `Reason: ${entry.denial_reason.slice(0, 120)}${entry.denial_reason.length > 120 ? '…' : ''}` : undefined,
    });
  }

  if (entry.workflow_status === 'returned' && entry.submitted_at) {
    events.push({
      label: 'Returned for revision',
      timestamp: entry.updated_at,
      icon: RotateCcw,
      iconClass: 'text-accent-amber',
    });
  }

  if (entry.revoked_at) {
    events.push({
      label: 'Revoked',
      timestamp: entry.revoked_at,
      icon: ShieldAlert,
      iconClass: 'text-accent-rose',
      detail: entry.revocation_reason ? `Reason: ${entry.revocation_reason.slice(0, 120)}${entry.revocation_reason.length > 120 ? '…' : ''}` : undefined,
    });
  }

  if (entry.closed_at) {
    events.push({
      label: entry.closure_type === 'mitigated' ? 'Closed as mitigated' : 'Closed',
      timestamp: entry.closed_at,
      icon: CheckCircle,
      iconClass: 'text-accent-emerald',
      detail: entry.mitigation_summary ? `Summary: ${entry.mitigation_summary.slice(0, 120)}${entry.mitigation_summary.length > 120 ? '…' : ''}` : undefined,
    });
  }

  if (entry.expired_at) {
    events.push({
      label: 'Expired',
      timestamp: entry.expired_at,
      icon: Clock,
      iconClass: 'text-text-secondary',
    });
  }

  return events.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
}

// ── Submit warning dialog ────────────────────────────────────────────────────

function SubmitWarningDialog({
  open,
  onClose,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Submit for Approval</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-text-secondary py-2">
          You are submitting for approval. Once submitted, the entry cannot be edited until an
          Approver returns it for revision.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={isPending}>
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Confirm Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function OrgRiskAcceptanceDetailPage() {
  useEffect(() => { document.title = 'Org Risk Response — VeriGRC'; }, []);

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, hasAnyRole } = useAuthStore();

  const { data: entry, isLoading, error } = useOrgRiskAcceptance(id);
  const submitMutation = useSubmitOrgRiskAcceptance();
  const resumeMutation = useResumeOrgDraft();

  // Action modal state — ORA.4: approve/deny/return/close-as-mitigated/revoke
  const [actionModal, setActionModal] = useState<'approve' | 'deny' | 'return' | 'close-as-mitigated' | 'revoke' | null>(null);
  const [submitWarningOpen, setSubmitWarningOpen] = useState(false);
  const [renewalOpen, setRenewalOpen] = useState(false);

  // ── Role derivation ──────────────────────────────────────────────────────

  const currentUserId = user?.id ?? null;
  const isApproverClass = hasAnyRole(['risk_manager', 'super_admin']);
  const isSubmitter = entry ? entry.submitted_by_user_id === currentUserId : false;
  const isSelfApproval = isSubmitter; // 4-eyes: submitter cannot approve own entry

  // ── Handlers ─────────────────────────────────────────────────────────────

  async function handleCopyLink() {
    // IF-3: copy /org-risk-acceptances/:id URL
    const url = `https://app.verigrc.com/org-risk-acceptances/${id}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied');
    } catch {
      toast.error('Failed to copy link');
    }
  }

  async function handleResumeDraft() {
    if (!id) return;
    try {
      await resumeMutation.mutateAsync(id);
      navigate(`/org-risk-acceptances/new?from=${id}`);
    } catch {
      toast.error('Failed to resume draft. Please try again.');
    }
  }

  async function handleSubmit() {
    if (!id) return;
    try {
      await submitMutation.mutateAsync(id);
      toast.success('Submitted for approval');
      setSubmitWarningOpen(false);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number } };
      const status = axiosErr?.response?.status;
      if (status === 409) {
        toast.error("This entry's state changed. Please refresh to see the latest.");
      } else if (status === 403) {
        toast.error("You don't have permission for this action");
      } else {
        toast.error('Failed to submit. Please try again.');
      }
      setSubmitWarningOpen(false);
    }
  }

  function handleActionSuccess(_updated: unknown) {
    setActionModal(null);
  }

  function handleRenewalSuccess(childEntry: unknown) {
    const child = childEntry as { id: string };
    navigate(`/org-risk-acceptances/${child.id}`);
  }

  // ── Loading / error states ────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <AlertCircle className="h-10 w-10 text-accent-rose mb-3" />
        <h2 className="text-lg font-semibold text-text-primary mb-1">Org risk response not found</h2>
        <p className="text-sm text-text-secondary mb-4">
          This entry may have been removed or you don't have access.
        </p>
        <Button variant="outline" onClick={() => navigate('/org-risk-acceptances')}>
          Back to Risk Register
        </Button>
      </div>
    );
  }

  // ── Action panel arm selection ────────────────────────────────────────────

  const showSubmitterDraftPanel = entry.workflow_status === 'draft' && isSubmitter;
  const showSubmitterReturnedPanel = entry.workflow_status === 'returned' && isSubmitter;
  const showSubmitterPendingBanner = entry.workflow_status === 'pending_approval' && isSubmitter && !isApproverClass;
  const showApproverPanel = entry.workflow_status === 'pending_approval' && isApproverClass;
  // ORA.4: APPROVED state action panel (close-as-mitigated + revoke)
  const showApprovedPanel = entry.workflow_status === 'approved' && isApproverClass;
  // 4-eyes for Revoke: disable when actor === submitter (NOT approver-of-record)
  const isSelfRevoke = currentUserId === entry.submitted_by_user_id;
  // ORA.5: renewal panel — submitter on approved entry with window open
  const showRenewalPanel = entry.workflow_status === 'approved' && entry.renewal_window_open === true && isSubmitter && !entry.superseded_by_acceptance_id;

  const timeline = deriveTimeline(entry);

  return (
    <div className="space-y-5">
      {/* ── Back nav ─────────────────────────────────────────────────────── */}
      <button
        onClick={() => navigate('/org-risk-acceptances')}
        className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Risk Register
      </button>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-text-primary truncate">
              {entry.title ?? 'Untitled Org Risk Response'}
            </h1>
            <RrStatusCell entry={entry} />
          </div>
          <div className="mt-1 flex items-center gap-4 flex-wrap text-xs text-text-secondary">
            {entry.submitted_at && (
              <span>
                Submitted{' '}
                {formatDistanceToNow(new Date(entry.submitted_at), { addSuffix: true })}
              </span>
            )}
            <span>
              Updated{' '}
              {formatDistanceToNow(new Date(entry.updated_at), { addSuffix: true })}
            </span>
          </div>
        </div>

        {/* Copy-to-clipboard (IF-3) */}
        <Button
          variant="outline"
          size="sm"
          className="shrink-0 h-8 text-xs gap-1.5"
          onClick={handleCopyLink}
        >
          <Copy className="h-3 w-3" />
          Copy link
        </Button>
      </div>

      {/* ── Action panel ─────────────────────────────────────────────────── */}

      {/* Arm 1: Submitter + DRAFT */}
      {showSubmitterDraftPanel && (
        <Card className="border-accent-cyan/30 bg-accent-cyan-dim/20">
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <p className="text-sm text-text-secondary">
              This draft is ready to edit or submit for approval.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/org-risk-acceptances/new?from=${entry.id}`)}
              >
                <FileEdit className="mr-1.5 h-3.5 w-3.5" />
                Edit Draft
              </Button>
              <Button
                size="sm"
                onClick={() => setSubmitWarningOpen(true)}
                disabled={submitMutation.isPending}
              >
                {submitMutation.isPending
                  ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  : <Send className="mr-1.5 h-3.5 w-3.5" />}
                Submit for Approval
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arm 2: Submitter + RETURNED */}
      {showSubmitterReturnedPanel && (
        <Card className="border-accent-amber/30 bg-accent-amber-dim/20">
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <div>
              <p className="text-sm font-medium text-text-primary">Returned for revision</p>
              <p className="text-xs text-text-secondary mt-0.5">
                An Approver returned this entry. Resume the draft to make changes.
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleResumeDraft}
              disabled={resumeMutation.isPending}
            >
              {resumeMutation.isPending
                ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                : <RotateCcw className="mr-1.5 h-3.5 w-3.5" />}
              Resume Draft
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Arm 3: Submitter + PENDING_APPROVAL banner (non-approver) */}
      {showSubmitterPendingBanner && (
        <Card className="border-accent-cyan/30">
          <CardContent className="flex items-center gap-3 py-4">
            <Clock className="h-4 w-4 text-accent-cyan shrink-0" />
            <p className="text-sm text-text-secondary">
              Awaiting Approver review. You will be notified when a decision is made.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Arms 4 + 5: Approver panel (PENDING_APPROVAL) */}
      {showApproverPanel && (
        <Card className="border-accent-amber/30">
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <p className="text-sm text-text-secondary">
              This entry is pending approval. Review the details below, then take action.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {/* Approve — disabled for 4-eyes */}
              <Button
                size="sm"
                disabled={isSelfApproval}
                title={
                  isSelfApproval
                    ? 'You cannot approve a Risk Response you submitted (4-eyes rule)'
                    : undefined
                }
                onClick={() => !isSelfApproval && setActionModal('approve')}
                className="bg-accent-emerald hover:bg-accent-emerald/80 text-white disabled:opacity-50"
              >
                <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
                Approve
              </Button>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => setActionModal('deny')}
              >
                <XCircle className="mr-1.5 h-3.5 w-3.5" />
                Deny
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setActionModal('return')}
              >
                <RotateCcw className="mr-1.5 h-3.5 w-3.5" />
                Return for Info
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arms 6 + 7: APPROVED state — Close as Mitigated + Revoke (ORA.4) */}
      {showApprovedPanel && (
        <Card className="border-accent-emerald/30 bg-accent-emerald-dim/10">
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <p className="text-sm text-text-secondary">
              This risk response has been approved. You may close it as mitigated or revoke the approval.
            </p>
            <div className="flex items-center gap-2 shrink-0">
              {/* Close as Mitigated — no 4-eyes */}
              <Button
                size="sm"
                className="bg-accent-emerald hover:bg-accent-emerald/80 text-white"
                onClick={() => setActionModal('close-as-mitigated')}
              >
                <CheckCircle className="mr-1.5 h-3.5 w-3.5" />
                Close as Mitigated
              </Button>
              {/* Revoke — 4-eyes: disabled when actor === submitter */}
              <Button
                size="sm"
                variant="destructive"
                disabled={isSelfRevoke}
                title={
                  isSelfRevoke
                    ? 'You cannot revoke an approval for a submission you created (4-eyes rule)'
                    : undefined
                }
                onClick={() => !isSelfRevoke && setActionModal('revoke')}
              >
                <XCircle className="mr-1.5 h-3.5 w-3.5" />
                Revoke
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arm 8: Submitter + APPROVED + renewal window open (ORA.5) */}
      {showRenewalPanel && (
        <Card className="border-accent-cyan/30 bg-accent-cyan-dim/20">
          <CardContent className="flex items-center justify-between gap-4 py-4">
            <div>
              <p className="text-sm font-medium text-text-primary">Renewal window open</p>
              <p className="text-xs text-text-secondary mt-0.5">
                This approval is approaching expiry. You may request a renewal.
              </p>
            </div>
            <Button size="sm" onClick={() => setRenewalOpen(true)}>
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              Request Renewal
            </Button>
          </CardContent>
        </Card>
      )}

      {/* ── Return comments section (state=RETURNED) ─────────────────────── */}
      {entry.workflow_status === 'returned' && (
        <Card className="border-accent-amber/40 bg-accent-amber-dim/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2 text-accent-amber">
              <RotateCcw className="h-4 w-4" />
              Returned for Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {entry.latest_return ? (
              <>
                <div className="rounded-md border border-border-subtle bg-bg-muted p-3 text-text-primary whitespace-pre-wrap break-words">
                  {entry.latest_return.return_comments}
                </div>
                <p className="text-xs text-text-secondary">
                  Returned{' '}
                  {formatDistanceToNow(new Date(entry.latest_return.returned_at), { addSuffix: true })}
                </p>
              </>
            ) : (
              <p className="text-text-secondary">
                This entry was returned for information. Comments unavailable.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* ── Entry fields display ────────────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-5">

          {/* Core risk info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Risk Information</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border-subtle">
                <FieldRow label="Title" value={entry.title} />
                <FieldRow label="Description" value={entry.description} />
                <FieldRow label="Risk Category" value={entry.risk_category?.replace(/_/g, ' ')} />
                {entry.category_other_specify && (
                  <FieldRow label="Category (Specify)" value={entry.category_other_specify} />
                )}
                <FieldRow label="Source" value={entry.source?.replace(/_/g, ' ')} />
                {entry.source_other_specify && (
                  <FieldRow label="Source (Specify)" value={entry.source_other_specify} />
                )}
                <FieldRow label="Potential Impact" value={entry.potential_impact} />
                <FieldRow label="Likelihood" value={entry.likelihood} />
                <FieldRow label="Impact Severity" value={entry.impact_severity} />
                <FieldRow label="Inherent Risk Rating" value={entry.inherent_risk_rating} />
                <BoolRow label="Inherent Risk Override" value={entry.inherent_risk_override} />
                {entry.inherent_risk_override && (
                  <FieldRow
                    label="Override Justification"
                    value={entry.inherent_risk_override_justification}
                  />
                )}
              </dl>
            </CardContent>
          </Card>

          {/* Acceptance & controls */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Acceptance & Controls</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border-subtle">
                <FieldRow label="Business Justification" value={entry.business_justification} />
                <DateRow label="Start Date" value={entry.acceptance_start_date} />
                <DateRow label="Expires" value={entry.acceptance_expires_at} />
                {entry.long_duration_justification && (
                  <FieldRow
                    label="Long Duration Justification"
                    value={entry.long_duration_justification}
                  />
                )}
                <FieldRow label="Compensating Controls" value={entry.compensating_controls} />
                {entry.compensating_controls && (
                  <FieldRow label="Residual Risk Rating" value={entry.residual_risk_rating} />
                )}
                <FieldRow label="Internal Stakeholders" value={entry.internal_stakeholders} />
                <FieldRow
                  label="External Evidence References"
                  value={entry.external_evidence_references}
                />
              </dl>
            </CardContent>
          </Card>

          {/* Linked entity */}
          {entry.linked_entity_type && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Linked Entity</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y divide-border-subtle">
                  <LinkedEntityDisplay
                    linkedEntityType={entry.linked_entity_type}
                    linkedEntityId={entry.linked_entity_id}
                  />
                  <FieldRow label="Context" value={entry.linked_entity_context} />
                </dl>
              </CardContent>
            </Card>
          )}

          {/* Decision info (approved / denied / revoked) */}
          {(entry.approved_at ?? entry.denied_at ?? entry.revoked_at ?? entry.closed_at) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Decision</CardTitle>
              </CardHeader>
              <CardContent>
                <dl className="divide-y divide-border-subtle">
                  <DateRow label="Approved at" value={entry.approved_at} />
                  <FieldRow label="Approval Notes" value={entry.approval_notes} />
                  <DateRow label="Denied at" value={entry.denied_at} />
                  <FieldRow label="Denial Reason" value={entry.denial_reason} />
                  <DateRow label="Revoked at" value={entry.revoked_at} />
                  <FieldRow label="Revocation Reason" value={entry.revocation_reason} />
                  <DateRow label="Closed at" value={entry.closed_at} />
                  <FieldRow label="Closure Type" value={entry.closure_type} />
                  <FieldRow label="Mitigation Summary" value={entry.mitigation_summary} />
                  <FieldRow
                    label="Mitigation Evidence"
                    value={entry.mitigation_evidence_references}
                  />
                  <DateRow label="Expired at" value={entry.expired_at} />
                </dl>
              </CardContent>
            </Card>
          )}
        </div>

        {/* ── Sidebar: audit timeline ───────────────────────────────────── */}
        <div className="space-y-5">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Audit Timeline</CardTitle>
            </CardHeader>
            <CardContent className="pb-3">
              {timeline.length === 0 ? (
                <p className="text-sm text-text-secondary">No events recorded.</p>
              ) : (
                <ol className="relative border-l border-border-subtle ml-2 space-y-4">
                  {timeline.map((event, i) => {
                    const Icon = event.icon;
                    return (
                      <li key={i} className="pl-5">
                        <span className="absolute -left-[9px] flex h-[18px] w-[18px] items-center justify-center rounded-full bg-bg-surface ring-2 ring-bg-base">
                          <Icon className={`h-3 w-3 ${event.iconClass}`} />
                        </span>
                        <p className="text-sm font-medium text-text-primary">{event.label}</p>
                        <time className="text-xs text-text-secondary">
                          {format(new Date(event.timestamp), 'PPp')}
                        </time>
                        {event.detail && (
                          <p className="mt-0.5 text-xs text-text-secondary italic">
                            {event.detail}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              )}
            </CardContent>
          </Card>

          {/* Meta card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <dt className="text-text-secondary">State</dt>
                  <dd><RrStatusCell entry={entry} /></dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-text-secondary">Created</dt>
                  <dd className="text-text-primary text-xs">
                    {format(new Date(entry.created_at), 'PP')}
                  </dd>
                </div>
                {entry.submitted_at && (
                  <div className="flex justify-between gap-2">
                    <dt className="text-text-secondary">Submitted</dt>
                    <dd className="text-text-primary text-xs">
                      {format(new Date(entry.submitted_at), 'PP')}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <dt className="text-text-secondary">Expires</dt>
                  <dd className="text-text-primary text-xs">
                    {/* ⚠ Slice 2: expiry is nullable (O1/V995) — NULL = "No expiry" for non-Accept responses. */}
                    {entry.acceptance_expires_at
                      ? format(new Date(entry.acceptance_expires_at), 'PP')
                      : <span className="italic text-text-faint">No expiry</span>}
                  </dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-text-secondary">Source</dt>
                  <dd className="text-text-primary text-xs">{entry.created_via ?? '—'}</dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── ORA.9: Renewal chain visualization ───────────────────────────── */}
      {entry.lineage && entry.lineage.length > 1 && (
        <OrgRenewalLineageSection
          currentEntryId={entry.id}
          lineage={entry.lineage}
        />
      )}

      {/* ── COLLAB-NOTIF S2: Notes ───────────────────────────────────────── */}
      <div className="mt-6">
        <ResourceNotes resourceType="org_risk_acceptance" resourceId={entry.id} host="detail" resourceLabel="risk response" />
      </div>

      {/* ── Modals ───────────────────────────────────────────────────────── */}

      {/* Submit warning dialog */}
      <SubmitWarningDialog
        open={submitWarningOpen}
        onClose={() => setSubmitWarningOpen(false)}
        onConfirm={handleSubmit}
        isPending={submitMutation.isPending}
      />

      {/* Action modal (approve | deny | return | close-as-mitigated | revoke) */}
      {actionModal && (
        <RiskAcceptanceActionModal
          action={actionModal}
          entry={entry}
          onSuccess={handleActionSuccess}
          onClose={() => setActionModal(null)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mutationHook={useOrgRiskAcceptanceAction as any}
        />
      )}

      {/* ORA.5: Request Renewal modal (Submitter on APPROVED parent with window open) */}
      {renewalOpen && (
        <RequestRenewalModal
          entry={entry}
          onSuccess={handleRenewalSuccess}
          onClose={() => setRenewalOpen(false)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          mutationHook={useRequestOrgRenewal as any}
        />
      )}
    </div>
  );
}
